/**
 * Tunables for VIRUTOPIA — adjust balance here.
 */

export const GAME_WIDTH = 1200;
export const GAME_HEIGHT = 800;

/** Base logical radius for player sprite frame (pixels); actual collision radius scales with growth */
export const PLAYER_BASE_RADIUS = 14;

/** Blood flow: added to horizontal drift each frame (world units ~ px at 60fps scaled by dt) */
export const FLOW_SPEED_BASE = 42;

/** Max simultaneous gameplay entities (excluding decorative particles) */
export const GAMEPLAY_ENTITY_CAP = 35;

/** Legacy alias — gameplay cap only */
export const ENTITY_CAP = GAMEPLAY_ENTITY_CAP;

/** Difficulty ramp: multiplier curves over survival seconds */
export const DIFFICULTY = {
  spawnIntervalMin: 0.35,
  spawnIntervalStart: 1.15,
  flowBonusPerMinute: 22,
  weightBiasPerMinute: 0.08,
  entityPressurePerMinute: 0.06,
};

/** Spawn weights by entity kind — scaled by difficulty bias toward threats */
export const SPAWN_WEIGHTS = {
  collectible: 48,
  wbc: 18,
  antibody: 14,
  platelet: 28,
  powerShield: 1.2,
  powerSpeed: 1.1,
  powerInvis: 1.0,
  powerSlow: 0.85,
};

/** Seconds between spawn attempts at difficulty 1 */
export const SPAWN_ATTEMPT_BASE_INTERVAL = 0.55;

/** Minimum distance from player center to spawn center (avoid cheap hits) */
export const SPAWN_CLEARANCE_MIN = 72;
export const SPAWN_CLEARANCE_RETRY = 6;

/** Player movement (baseline max speeds; Size tiers reduce via SIZE_SPEED) */
export const PLAYER = {
  accel: 1280,
  maxSpeedY: 345,
  maxSpeedX: 268,
  /** Slightly stronger push against bloodstream drift (easier to peel off hunters) */
  flowResistance: 0.42,
  drag: 6.5,
};

/** Growth: radius per pickup + score multiplier (move speed uses SIZE_SPEED by radius). */
export const GROWTH = {
  radiusPerCollect: 2,
  /** Lets HUD reach ~100 for clone power (14 + 43×2 = 100). */
  maxRadius: 100,
  scoreMultPerStack: 0.12,
  maxStacks: 43,
};

/**
 * Move-speed multiplier vs collision radius (linear between breakpoints).
 * 100% at small Size → 95% @50 → 90% @80 → 85% @100.
 */
export const SIZE_SPEED = {
  breakpointLow: 50,
  breakpointMid: 80,
  mulLow: 0.95,
  mulMid: 0.9,
  mulHigh: 0.85,
};

/** Scoring */
export const SCORE = {
  /** Applied to survival drift, growth streaming, and collectible pickups (25% slower ⇒ 0.75). */
  growthRateMul: 0.75,
  survivalPerSecond: 12,
  collectibleBase: 55,
  growthBonusPerStack: 8,
};

/** Entity radii (logical) */
export const ENTITY_RADIUS = {
  collectible: 8,
  wbc: 11,
  antibody: 13,
  platelet: 9,
  powerUp: 10,
};

/**
 * Threat overlap uses a smaller effective player radius: logical radius rises with growth stacks,
 * but the sprite scale grows slower, so raw `player.radius` feels like an oversized hit ring.
 */
export const HITBOX = {
  /** Fraction of radius growth (above PLAYER_BASE_RADIUS) counted vs WBC / antibody / platelet. */
  threatGrowthScale: 0.32,
};

/** WBC seeks player / decoys (kept clearly below typical player max speeds so dodging stays viable) */
export const WBC = {
  seekSpeed: 78,
  seekSpeedDifficulty: 0.75,
  /** Extra multiplier while chasing decoy clones (peels hunters away faster). */
  decoySeekMul: 1.35,
};

/** Shield charges (hits consume one stage). Game starts below max so pickups matter. */
export const SHIELD = {
  maxStages: 5,
  startStages: 3,
};

/**
 * White blood cells: hard cap + **elapsed-time** spacing between spawns (score does not refill faster).
 */
export const WBC_SPAWN_CAP = {
  hardMax: 15,
  /** Seconds between one WBC spawn and when another may roll (random range). */
  spawnSpacingMin: 2.8,
  spawnSpacingMax: 5.4,
  /** Over this many minutes, spacing lerps toward `spawnSpacingFloor`. */
  rampMinutes: 14,
  spawnSpacingFloor: 1.05,
  /** After clone blast clears hunters, wait before new WBC may spawn. */
  afterDecoyPurgeDelaySec: 1.6,
};

/** Space-bar clone bait: flee left; hunters target decoys until they despawn. */
export const DECOY = {
  radiusThreshold: 100,
  /** Radius / HUD Mass subtracted from the player when clones spawn (virus shrinks accordingly). */
  massCost: 100,
  cloneCount: 4,
  fleeSpeedX: -310,
  wanderVyMax: 72,
  cooldownSec: 42,
};

/** Antibody: slow drifting obstacle */
export const ANTIBODY = {
  speedX: -20,
  speedYRange: 35,
};

/** Platelet: mostly flow + wobble */
export const PLATELET = {
  wobbleSpeed: 2.2,
  wobbleAmp: 28,
};

/** Power-up durations (seconds) */
export const POWER_UP = {
  shield: 5.5,
  speedBoost: 4.2,
  invisibility: 5.0,
  slowMotion: 3.8,
};

export const POWER_SLOW_MULT = 0.45;

/** Speed boost multiplier on player max speed */
export const SPEED_BOOST_MULT = 1.55;

/** Parallax particles (decorative; capped separately from gameplay entities) */
export const PARTICLES = {
  maxActive: 35,
  speedMin: 18,
  speedMax: 95,
  alpha: 0.32,
};

/** Spawn / perf: ease load when FPS dips */
export const PERFORMANCE = {
  lowFpsThreshold: 45,
  spawnIntervalLowFpsMult: 1.55,
  maxSpawnBurstWhenLowFps: 1,
  maxSpawnBurstNormal: 4,
};

/** Screen juice + shield tuning */
export const FEEL = {
  shakeGrowthMs: 72,
  shakeGrowth: 0.0032,
  shakeShieldMs: 128,
  shakeShield: 0.0072,
};

/** Sprite sheet frame size (must match atlas builder) */
export const ATLAS_FRAME = 48;

/** Debug */
export const DEBUG = {
  spawnLogIntervalSec: 4,
};
